import React, { useMemo } from 'react';
import { ValueAxis, ArgumentAxis, Series, Legend } from 'devextreme-react/chart';
import { NuvoBarChart } from '@nuvolo/nuux-chart/components/NuvoBarChart';
import type { NuvoPieChartProps } from '@nuvolo/nuux-chart/components/NuvoPieChart';
import { DashboardStatsPoint } from '#/graphql/types';
import { maxBy } from 'lodash';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import { NuvoText } from '@nuvolo/nuux-core/components/NuvoText';
import FAIcon from '#/common/components/FAIcon';

export interface BarChartProps {
  title?: string;
  noDataText?: string;
  dataSource: (DashboardStatsPoint | null)[] | undefined | null;
  startingDataColor?: NuvoPieChartProps['startingDataColor'];
  setup: {
    valueAxis: string;
    argumentAxis: string;
  };
  onItemClick?: (item: DashboardStatsPoint) => void;
  yTickInterval?: number;
}

const BarChart: React.FC<BarChartProps> = ({
  title,
  noDataText = 'No data to display!',
  dataSource,
  setup,
  onItemClick,
  startingDataColor = 'red',
  yTickInterval,
}) => {
  const ds = useMemo(() => {
    if (!dataSource) {
      return [];
    }

    return dataSource?.map((v) => ({
      ...v,
      value: v?.value || 0,
    }));
  }, [dataSource]);

  const hasData = ds && ds.length > 0;

  if (!hasData) {
    return (
      <NuvoBox
        height="100%"
        minHeight="300px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        flex={1}>
        <FAIcon icon="shapes" size="xl" />
        <NuvoText type="body">{noDataText}</NuvoText>
      </NuvoBox>
    );
  }

  return (
    <NuvoBarChart
      showTooltip
      title={title}
      startingDataColor={startingDataColor}
      dataSource={ds}
      onPointClick={(e) => onItemClick?.(e.target.data as DashboardStatsPoint)}>
      <ValueAxis
        title={setup.valueAxis}
        tickInterval={yTickInterval}
        visualRange={{
          endValue: (yTickInterval ?? 0) + (maxBy(ds, (item) => item.value)?.value || 0),
        }}
      />
      <ArgumentAxis
        title={setup.argumentAxis}
        label={{
          rotationAngle: 45,
          indentFromAxis: 0,
          overlappingBehavior: 'rotate',
        }}
      />
      <Series
        argumentField="label"
        valueField="value"
        name={setup.valueAxis}
        label={{
          visible: true,
        }}
      />
    </NuvoBarChart>
  );
};

export default React.memo(BarChart);
